import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, MapPin, Phone } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

function fmtItalianDate(dateStr) {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("it-IT", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

export default function CancelBookingPage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get(`/bookings/cancel/${token}`)
            .then((r) => setInfo(r.data))
            .catch((e) => setError(formatApiError(e.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, [token]);

    const handleCancel = async () => {
        setSubmitting(true);
        try {
            await api.post(`/bookings/cancel/${token}`);
            setDone(true);
            toast.success("Prenotazione annullata. Ti abbiamo inviato una email di conferma.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-magni-cream">
            <Toaster position="top-center" richColors />
            <SiteHeader />

            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
                {loading && (
                    <div className="flex items-center justify-center py-24 text-magni-muted">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        Caricamento prenotazione...
                    </div>
                )}

                {error && !loading && (
                    <div className="bg-white border border-magni-light p-10 text-center">
                        <XCircle className="w-12 h-12 text-magni-navy mx-auto mb-4 opacity-60" />
                        <h1 className="font-serif-display text-3xl text-magni-navy mb-3">
                            Link non valido
                        </h1>
                        <p className="text-magni-muted mb-6" data-testid="cancel-error">{error}</p>
                        <Link to="/" className="magni-btn-secondary inline-block" data-testid="back-home-btn">
                            Torna alla home
                        </Link>
                    </div>
                )}

                {info && !loading && !done && (
                    <div className="bg-white border border-magni-light p-10">
                        <div className="overline mb-3" style={{ color: "#dc3545" }}>Annullamento prenotazione</div>
                        <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy mb-4">
                            Vuoi davvero annullare la prenotazione?
                        </h1>
                        <p className="text-magni-muted mb-8">
                            Stai per annullare la sessione di prova MagniStretch® di{" "}
                            <strong>{info.first_name} {info.last_name}</strong>.
                        </p>

                        <div className="bg-magni-cream border border-magni-light p-5 text-sm space-y-2 mb-8">
                            <div>
                                <span className="overline block">Quando</span>
                                {fmtItalianDate(info.slot_date)} alle {info.slot_time}
                            </div>
                            <div>
                                <span className="overline block">Dove</span>
                                <strong>{info.store?.name}</strong>
                                <div className="text-magni-muted text-xs mt-1 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {info.store?.address}, {info.store?.city}
                                </div>
                                {info.store?.phone && (
                                    <div className="text-magni-muted text-xs mt-1 flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {info.store.phone}
                                    </div>
                                )}
                            </div>
                            <div>
                                <span className="overline block">Stato attuale</span>
                                {info.status === "confirmed" ? "Confermata" : info.status}
                            </div>
                        </div>

                        {info.status === "cancelled" ? (
                            <div className="bg-magni-cream border border-magni-light p-5 text-center text-magni-muted">
                                Questa prenotazione risulta già annullata.
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    data-testid="confirm-cancel-btn"
                                    onClick={handleCancel}
                                    disabled={submitting}
                                    className="magni-btn-primary"
                                    style={{ background: "#dc3545", borderColor: "#dc3545" }}
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
                                            Annullamento...
                                        </>
                                    ) : (
                                        "Sì, annulla prenotazione"
                                    )}
                                </button>
                                <Link to="/" className="magni-btn-secondary text-center" data-testid="keep-booking-btn">
                                    No, mantieni
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {done && (
                    <div className="bg-white border border-magni-light p-10 text-center" data-testid="cancel-success">
                        <CheckCircle2 className="w-12 h-12 text-magni-navy mx-auto mb-4" />
                        <h1 className="font-serif-display text-3xl text-magni-navy mb-3">
                            Prenotazione annullata
                        </h1>
                        <p className="text-magni-muted mb-6">
                            Ti abbiamo inviato una email di conferma. Saremo felici di riaccoglierti quando vorrai.
                        </p>
                        <Link to="/" className="magni-btn-primary inline-block">
                            Prenota una nuova prova
                        </Link>
                    </div>
                )}
            </section>

            <SiteFooter />
        </div>
    );
}
